import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, ImageList, ImageListItem, useTheme, useMediaQuery, Modal, IconButton, Button, Dialog, DialogContent, CircularProgress } from '@mui/material';
import { getGameImages } from '../../services/api';
import { getSteamGameVideos } from '../../services/SteamApi';
import ImageUploadButton from './ImageUploadButton';
import { Link } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { isSuperuser } from '../../services/auth';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { Image } from '../../types/Game';
import CustomVideoPlayer from './CustomVideoPlayer';

interface SteamVideo {
  id: number;
  name: string;
  thumbnail: string;
  dash_av1?: string;
  dash_h264?: string;
  hls_h264?: string;
  webm?: {
    480: string;
    max: string;
  };
  mp4?: {
    480: string;
    max: string;
  };
  highlight: boolean;
}

interface SteamScreenshot {
  id: number;
  path_thumbnail: string;
  path_full: string;
}

interface SelectedMedia {
  type: 'image' | 'video' | 'steam-screenshot';
  url: string;
  original_url?: string | null;
  video_url?: string;
}

interface GameGallerySectionProps {
  gameId: number;
  initialImages?: Image[];
  totalCount?: number;
  steamId?: string;
}

const GameGallerySection: React.FC<GameGallerySectionProps> = ({ 
  gameId, 
  initialImages,
  totalCount: initialTotalCount,
  steamId
}) => {
  const [images, setImages] = useState<Image[]>(initialImages || []);
  const [videos, setVideos] = useState<SteamVideo[]>([]);
  const [steamScreenshots, setSteamScreenshots] = useState<SteamScreenshot[]>([]);
  const [totalCount, setTotalCount] = useState<number>(initialTotalCount || 0);
  const [selectedMedia, setSelectedMedia] = useState<SelectedMedia | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isUserSuperuser, setIsUserSuperuser] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [remainingCount, setRemainingCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const thumbnailsRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      {
        threshold: 0.7, // 70%以上表示されたら発火
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // セクションが表示されたときに動画を自動再生
  useEffect(() => {
    if (isInView && currentIndex < videos.length && !dialogOpen) {
      const video = videos[currentIndex];
      const videoUrl = video.hls_h264 || video.mp4?.['480'] || video.webm?.['480'] || '';
      setSelectedMedia({
        type: 'video',
        url: video.thumbnail,
        video_url: videoUrl.replace('http://', 'https://')
      });
    }
  }, [isInView, currentIndex, videos, dialogOpen]);

  const fetchImages = async () => {
    if (initialImages && initialTotalCount) {
      // 最初の2つの画像の順序を入れ替え
      if (initialImages.length >= 2) {
        const swappedImages = [...initialImages];
        [swappedImages[0], swappedImages[1]] = [swappedImages[1], swappedImages[0]];
        setImages(swappedImages);
      } else {
        setImages(initialImages);
      }
      setRemainingCount(initialTotalCount - initialImages.length);
      setOffset(initialImages.length);
      return;
    }

    try {
      const response = await getGameImages(gameId, 5, 0);
      setImages(response.results);
      setTotalCount(response.count);
      setRemainingCount(response.count - response.results.length);
      setOffset(response.results.length);
    } catch (error) {
      console.error('画像の取得に失敗しました:', error);
      setImages([]);
      setTotalCount(0);
      setRemainingCount(0);
      setOffset(0);
    }
  };

  const fetchSteamVideos = async () => {
    if (!steamId) return;
    
    try {
      const { videos, screenshots } = await getSteamGameVideos(steamId);
      // サムネイルURLを変換
      const processedVideos = videos.map(video => ({
        ...video,
        thumbnail: video.thumbnail.replace('movie.293x165.jpg', 'movie_full.jpg')
      }));
      setVideos(processedVideos);
      setSteamScreenshots(screenshots);
    } catch (error) {
      console.error('Steamのメディア取得に失敗しました:', error);
    }
  };

  const handleLoadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const response = await getGameImages(gameId, 20, offset);
      setImages(prev => [...prev, ...response.results]);
      setOffset(prev => prev + response.results.length);
      setRemainingCount(prev => prev - response.results.length);
      return response.results.length > 0;
    } catch (error) {
      console.error('追加画像の取得に失敗しました:', error);
      return false;
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      fetchImages();
      if (steamId) {
        fetchSteamVideos();
      }
      const superuser = await isSuperuser();
      setIsUserSuperuser(superuser);
    };
    init();
  }, [gameId, steamId]);

  const handleUploadComplete = (imageUrls: string[]) => {
    // 全ての画像のアップロードが完了してから画像一覧を再取得
    fetchImages();
  };

  const handleMediaClick = (type: 'image' | 'video' | 'steam-screenshot', media: Image | SteamVideo | SteamScreenshot, index: number) => {
    setCurrentIndex(index);
    if (type === 'video') {
      const video = media as SteamVideo;
      // HLS形式を優先的に使用
      const videoUrl = video.hls_h264 || video.mp4?.max || video.webm?.max || video.mp4?.['480'] || video.webm?.['480'] || '';

      // 先にHTTPSに変換してからステート更新
      const secureVideoUrl = videoUrl.replace('http://', 'https://');

      setSelectedMedia({
        type: 'video',
        url: video.thumbnail,
        video_url: secureVideoUrl
      });
    } else if (type === 'steam-screenshot') {
      const screenshot = media as SteamScreenshot;
      setSelectedMedia({
        type: 'image',
        url: screenshot.path_full,
        original_url: screenshot.path_full
      });
    } else if (type === 'image') {
      const image = media as Image;
      setSelectedMedia({
        type: 'image',
        url: getImageUrl(image),
        original_url: getOriginalUrl(image)
      });
    }
    setDialogOpen(true);
  };

  const handleCloseModal = () => {
    setDialogOpen(false);
    setSelectedMedia(null);
  };

  const handleViewOriginal = (e: React.MouseEvent, url: string) => {
    e.stopPropagation();
    window.open(url, '_blank');
  };

  const getImageUrl = (image: Image) => {
    if (image.is_igdb) {
      return `https://images.igdb.com/igdb/image/upload/t_720p/${image.image_id}.jpg`;
    }

    // カスタム画像の場合
    const basePath = `${process.env.REACT_APP_R2_PUBLIC_URL}/images/games/${gameId}`;
    const webpPath = `${image.image_id}_opt.webp`;
    return `${basePath}/${webpPath}`;
  };

  const getOriginalUrl = (image: Image) => {
    if (image.is_igdb) {
      return null;  // IGDBの画像にはオリジナル画像のURLは不要
    }

    if (image.original_format > 0) {
      const basePath = `${process.env.REACT_APP_R2_PUBLIC_URL}/images/games/${gameId}`;
      const formatExtension = {1: 'jpg', 2: 'png', 3: 'gif', 4: 'webp'}[image.original_format];
      return `${basePath}/${image.image_id}.${formatExtension}`;
    }

    return null;
  };

  const scrollThumbnailIntoView = (index: number) => {
    // 動画、Steamスクリーンショット、カスタム画像に対応するためのID取得
    let thumbnailId;
    if (index < videos.length) {
      thumbnailId = `thumbnail-video-${videos[index].id}`;
    } else if (index < videos.length + steamScreenshots.length) {
      const screenshotIndex = index - videos.length;
      thumbnailId = `thumbnail-steam-screenshot-${steamScreenshots[screenshotIndex].id}`;
    } else {
      const imageIndex = index - videos.length - steamScreenshots.length;
      if (!images[imageIndex]) return;
      thumbnailId = `thumbnail-${images[imageIndex].id}`;
    }

    const thumbnail = document.getElementById(thumbnailId);
    if (thumbnail) {
      // スクロール位置を計算
      const container = thumbnail.parentElement;
      if (container) {
        const containerWidth = container.clientWidth;
        const thumbnailWidth = thumbnail.clientWidth;
        const scrollPosition = thumbnail.offsetLeft - (containerWidth / 2) + (thumbnailWidth / 2);
        
        container.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  // currentIndexが変更されたときに自動スクロールを実行
  useEffect(() => {
    if (images && images.length > 0) {
      scrollThumbnailIntoView(currentIndex);
    }
  }, [currentIndex, images]);

  const handlePrevImage = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (currentIndex > 0 && images.length > 0) {
      setCurrentIndex(prevIndex => prevIndex - 1);
    }
  };

  const handleNextImage = async (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (images.length > 0 && currentIndex < images.length - 1) {
      setCurrentIndex(prevIndex => prevIndex + 1);

      // 最後から2番目の画像に到達し、まだ読み込む画像が残っている場合
      if (currentIndex >= images.length - 2 && remainingCount > 0) {
        await handleLoadMore();
      }
    }
  };

  // スライドショーのサムネイルをスクロールさせる関数
  const scrollDialogThumbnails = () => {
    if (!thumbnailsRef.current) return;

    const container = thumbnailsRef.current;
    let thumbnailId;
    if (currentIndex < videos.length) {
      thumbnailId = `dialog-thumbnail-video-${videos[currentIndex].id}`;
    } else if (currentIndex < videos.length + steamScreenshots.length) {
      const screenshotIndex = currentIndex - videos.length;
      thumbnailId = `dialog-thumbnail-steam-screenshot-${steamScreenshots[screenshotIndex].id}`;
    } else {
      const imageIndex = currentIndex - videos.length - steamScreenshots.length;
      if (!images[imageIndex]) return;
      thumbnailId = `dialog-thumbnail-${images[imageIndex].id}`;
    }

    const thumbnail = document.getElementById(thumbnailId) as HTMLElement;
    if (thumbnail) {
      const containerWidth = container.offsetWidth;
      const thumbnailWidth = thumbnail.offsetWidth;
      const scrollPosition = thumbnail.offsetLeft - (containerWidth / 2) + (thumbnailWidth / 2);

      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  };

  // currentIndexが変更されたときにスライドショーのサムネイルもスクロール
  useEffect(() => {
    if (dialogOpen) {
      scrollDialogThumbnails();
    }
  }, [currentIndex, dialogOpen]);

  const handleThumbnailClick = (type: 'image' | 'video' | 'steam-screenshot', media: Image | SteamVideo | SteamScreenshot, index: number) => {
    setCurrentIndex(index);
    if (type === 'video') {
      const video = media as SteamVideo;
      // HLS形式を優先的に使用
      const videoUrl = video.hls_h264 || video.mp4?.max || video.webm?.max || video.mp4?.['480'] || video.webm?.['480'] || '';
      const secureVideoUrl = videoUrl.replace('http://', 'https://');

      setSelectedMedia({
        type: 'video',
        url: video.thumbnail,
        video_url: secureVideoUrl
      });
    } else if (type === 'steam-screenshot') {
      const screenshot = media as SteamScreenshot;
      setSelectedMedia({
        type: 'image',
        url: screenshot.path_full,
        original_url: screenshot.path_full
      });
    } else if (type === 'image') {
      const image = media as Image;
      setSelectedMedia({
        type: 'image',
        url: getImageUrl(image),
        original_url: getOriginalUrl(image)
      });
    }
  };

  const hasAnyMedia = (images && images.length > 0) || 
                     (videos && videos.length > 0) || 
                     (steamScreenshots && steamScreenshots.length > 0);

  if (!hasAnyMedia) {
    if (process.env.NODE_ENV !== 'development' && !isUserSuperuser) {
      return null;
    }

    return (
      <Box sx={{ mt: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 2 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" component="h2" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
              ギャラリー
            </Typography>
            <IconButton
              component={Link}
              to={`/game/${gameId}/images`}
              size="small"
              sx={{ 
                ml: 0.5,
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                }
              }}
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>
          {(process.env.NODE_ENV === 'development' || isUserSuperuser) && (
            <ImageUploadButton 
              gameId={gameId}
              onUploadComplete={handleUploadComplete}
              onError={(error) => console.error(error)}
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">
          このゲームの画像はまだ登録されていません。
        </Typography>
      </Box>
    );
  }

  return (
    <Box ref={sectionRef} sx={{ mt: { xs: 2, sm: 2 } }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 1 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="h2" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
            ギャラリー
          </Typography>
          <Box
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: '0.7rem',
              padding: '2px 8px',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {videos.length + steamScreenshots.length + totalCount}
          </Box>
          <IconButton
            component={Link}
            to={`/game/${gameId}/images`}
            size="small"
            sx={{ 
              ml: 0.5,
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
              }
            }}
          >
            <NavigateNextIcon />
          </IconButton>
        </Box>
        {(process.env.NODE_ENV === 'development' || isUserSuperuser) && (
          <ImageUploadButton 
            gameId={gameId}
            onUploadComplete={handleUploadComplete}
            onError={(error) => console.error(error)}
          />
        )}
      </Box>

      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16/9',
            borderRadius: 1,
            overflow: 'hidden',
            cursor: 'pointer',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            '&:hover': {
              '& img': {
                opacity: 0.9,
              },
            },
          }}
          onClick={() => {
            const allMedia = [...videos, ...steamScreenshots, ...images];
            if (allMedia.length > 0) {
              const currentMedia = allMedia[currentIndex];
              const mediaType = 'thumbnail' in currentMedia ? 'video' : 
                         'path_thumbnail' in currentMedia ? 'steam-screenshot' : 'image';
              
              // 既に動画が再生中の場合は、コンテナクリックでは何もしない
              if (mediaType === 'video' && selectedMedia?.type === 'video' && !dialogOpen) {
                return;
              }
              
              if (mediaType === 'video') {
                const video = currentMedia as SteamVideo;
                setDialogOpen(true);
                const videoUrl = video.hls_h264 || video.mp4?.max || video.webm?.max || '';
                setSelectedMedia({
                  type: 'video',
                  url: video.thumbnail,
                  video_url: videoUrl.replace('http://', 'https://')
                });
              } else {
                setDialogOpen(true);
                handleMediaClick(mediaType, currentMedia, currentIndex);
              }
            }
          }}
        >
          {videos.length > 0 || images.length > 0 || steamScreenshots.length > 0 ? (
            <>
              {currentIndex < videos.length ? (
                <>
                  {selectedMedia?.type === 'video' && !dialogOpen ? (
                    <Box 
                      onClick={(e) => {
                        // プレイヤーUI操作時のクリックイベントが親要素に伝播しないようにする
                        e.stopPropagation();
                      }}
                      sx={{ 
                        width: '100%', 
                        height: '100%',
                        position: 'relative'
                      }}
                    >
                      <CustomVideoPlayer
                        src={selectedMedia.video_url?.replace('http://', 'https://') || ''}
                        title={videos[currentIndex].name}
                        autoPlay={true}
                        initialMuted={true}
                        showControls={true}
                        compact={true}
                        thumbnailUrl={videos[currentIndex].thumbnail}
                        onFullscreen={() => {
                          const video = videos[currentIndex];
                          setDialogOpen(true);
                          const videoUrl = video.hls_h264 || video.mp4?.max || video.webm?.max || '';
                          setSelectedMedia({
                            type: 'video',
                            url: video.thumbnail,
                            video_url: videoUrl.replace('http://', 'https://')
                          });
                        }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ position: 'relative' }}>
                      <img
                        src={videos[currentIndex].thumbnail}
                        alt={videos[currentIndex].name}
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          transition: 'opacity 0.2s',
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          padding: '0px 8px',
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'white', lineHeight: 1.2 }}>
                          {videos[currentIndex].name}
                        </Typography>
                      </Box>
                      {/* <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          borderRadius: '50%',
                          width: 64,
                          height: 64,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <PlayArrowIcon sx={{ fontSize: 40, color: 'white' }} />
                      </Box> */}
                    </Box>
                  )}
                </>
              ) : currentIndex < videos.length + steamScreenshots.length ? (
                <img
                  src={steamScreenshots[currentIndex - videos.length].path_thumbnail}
                  alt="Screenshot"
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    transition: 'opacity 0.2s',
                  }}
                />
              ) : (
                <img
                  src={getImageUrl(images[currentIndex - videos.length - steamScreenshots.length])}
                  alt={images[currentIndex - videos.length - steamScreenshots.length].description || ''}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    transition: 'opacity 0.2s',
                  }}
                />
              )}
            </>
          ) : null}

          <Box
            sx={{
              position: 'absolute',
              bottom: { xs: 4, sm: 8 },
              left: { xs: 4, sm: 8 },
              display: 'flex',
              gap: 0.5,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              padding: { xs: '2px 8px', sm: '4px 8px' },
              borderRadius: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'white',
                fontSize: { xs: '0.5rem', sm: '0.7rem' },
              }}
            >
              {currentIndex + 1} / {videos.length + steamScreenshots.length + totalCount}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 1,
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            pb: 1,
            '&::-webkit-scrollbar': {
              height: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.15)',
              borderRadius: '3px',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.25)',
              },
            },
          }}
        >
          {videos.map((video, index) => (
            <Box
              key={`video-${video.id}`}
              id={`thumbnail-video-${video.id}`}
              sx={{
                position: 'relative',
                cursor: 'pointer',
              }}
              onClick={() => handleThumbnailClick('video', video, index)}
            >
              <Box
                component="img"
                src={video.thumbnail}
                alt={video.name}
                sx={{
                  width: '120px',
                  height: '67.5px',
                  objectFit: 'cover',
                  borderRadius: 1,
                  opacity: currentIndex === index ? 1 : 0.75,
                  border: currentIndex === index ? '1px solid rgba(255, 255, 255, 0.5)' : 'none',
                  transition: 'opacity 0.2s',
                  '&:hover': {
                    opacity: 1,
                  },
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  borderRadius: '50%',
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PlayArrowIcon sx={{ fontSize: 16, color: 'white' }} />
              </Box>
            </Box>
          ))}
          {steamScreenshots.map((screenshot, index) => (
            <Box
              key={`steam-screenshot-${screenshot.id}`}
              id={`thumbnail-steam-screenshot-${screenshot.id}`}
              component="img"
              src={screenshot.path_thumbnail}
              alt="Screenshot"
              onClick={() => handleThumbnailClick('steam-screenshot', screenshot, videos.length + index)}
              sx={{
                width: '120px',
                height: '67.5px',
                objectFit: 'cover',
                cursor: 'pointer',
                borderRadius: 1,
                opacity: currentIndex === (videos.length + index) ? 1 : 0.75,
                border: currentIndex === (videos.length + index) ? '1px solid rgba(255, 255, 255, 0.5)' : 'none',
                transition: 'opacity 0.2s',
                '&:hover': {
                  opacity: 1,
                },
              }}
            />
          ))}
          {images.map((image, index) => (
            <Box
              key={image.id}
              id={`thumbnail-${image.id}`}
              component="img"
              src={getImageUrl(image)}
              alt={image.description || ''}
              onClick={() => setCurrentIndex(videos.length + steamScreenshots.length + index)}
              sx={{
                width: '120px',
                height: '67.5px',
                objectFit: 'cover',
                cursor: 'pointer',
                borderRadius: 1,
                opacity: currentIndex === (videos.length + steamScreenshots.length + index) ? 1 : 0.75,
                border: currentIndex === (videos.length + steamScreenshots.length + index) ? '1px solid rgba(255, 255, 255, 0.5)' : 'none',
                transition: 'opacity 0.2s',
                '&:hover': {
                  opacity: 1,
                },
              }}
            />
          ))}
          {remainingCount > 0 && (
            <Box
              onClick={handleLoadMore}
              sx={{
                width: '120px',
                height: '67.5px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 1,
                cursor: 'pointer',
                color: 'white',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              {loadingMore ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" component="div" sx={{ color: 'text.secondary' }}>
                    +{remainingCount}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseModal}
        maxWidth={false}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(12px)',
            overflow: 'hidden',
            width: { xs: '100%', sm: '1280px' },
            height: { xs: '100%', sm: '840px' },
            m: { xs: 0, sm: 2 },
          }
        }}
      >
        <IconButton
          onClick={handleCloseModal}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: 'white',
            zIndex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent
          sx={{
            p: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            '&.MuiDialogContent-root': {
              overflow: 'hidden',
            }
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 0,
              pt: { xs: 2, sm: 0 },
            }}
          >
            {selectedMedia?.type === 'video' ? (
              <Box sx={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' }}>
                <CustomVideoPlayer
                  src={selectedMedia.video_url || ''}
                  title={currentIndex < videos.length ? videos[currentIndex].name : ''}
                  autoPlay={true}
                  initialMuted={true}
                  showControls={true}
                  compact={false}
                  thumbnailUrl={selectedMedia.url}
                />
              </Box>
            ) : (
              selectedMedia && (
                <Box
                  component="img"
                  src={selectedMedia.url}
                  alt=""
                  sx={{
                    minWidth: isMobile ? '100%' : '512px',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    outline: 'none',
                    userSelect: 'none',
                  }}
                />
              )
            )}
          </Box>

          <Box
            sx={{
              mt: 'auto',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 1,
              pt: 2,
              pb: 2,
              height: '120px',
            }}
          >
            <IconButton
              onClick={handlePrevImage}
              disabled={currentIndex === 0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: isMobile ? '32px' : '48px',
                height: '100px',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                cursor: currentIndex === 0 ? 'default' : 'pointer',
                opacity: currentIndex === 0 ? 0.5 : 1,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: currentIndex === 0 ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.7)',
                },
              }}
            >
              <ArrowBackIosNewIcon sx={{ color: 'white' }} />
            </IconButton>

            <Box
              ref={thumbnailsRef}
              sx={{
                flex: 1,
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                WebkitUserDrag: 'none',
                userSelect: 'none',
                '& img': {
                  userSelect: 'none',
                  WebkitUserDrag: 'none',
                }
              }}
            >
              {videos.map((video, index) => (
                <Box
                  key={`dialog-video-${video.id}`}
                  id={`dialog-thumbnail-video-${video.id}`}
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleMediaClick('video', video, index)}
                >
                  <Box
                    component="img"
                    src={video.thumbnail}
                    alt={video.name}
                    sx={{
                      width: isMobile ? '90px' : '160px',
                      height: isMobile ? '60px' : '90px',
                      objectFit: 'cover',
                      borderRadius: 1,
                      opacity: currentIndex === index ? 1 : 0.75,
                      border: currentIndex === index ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
                      transition: 'opacity 0.2s',
                      '&:hover': {
                        opacity: 1,
                      },
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      borderRadius: '50%',
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PlayArrowIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Box>
                </Box>
              ))}
              {steamScreenshots.map((screenshot, index) => (
                <Box
                  key={`dialog-steam-screenshot-${screenshot.id}`}
                  id={`dialog-thumbnail-steam-screenshot-${screenshot.id}`}
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleMediaClick('steam-screenshot', screenshot, videos.length + index)}
                >
                  <Box
                    component="img"
                    src={screenshot.path_thumbnail}
                    alt="Screenshot"
                    sx={{
                      width: isMobile ? '90px' : '160px',
                      height: isMobile ? '60px' : '90px',
                      objectFit: 'cover',
                      borderRadius: 1,
                      opacity: currentIndex === (videos.length + index) ? 1 : 0.75,
                      border: currentIndex === (videos.length + index) ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
                      transition: 'opacity 0.2s',
                      '&:hover': {
                        opacity: 1,
                      },
                    }}
                  />
                </Box>
              ))}
              {images.map((image, index) => (
                <Box
                  key={image.id}
                  id={`dialog-thumbnail-${image.id}`}
                  component="img"
                  src={getImageUrl(image)}
                  alt={image.description || ''}
                  onClick={() => handleMediaClick('image', image, videos.length + steamScreenshots.length + index)}
                  sx={{
                    width: isMobile ? '90px' : '160px',
                    height: isMobile ? '60px' : '90px',
                    objectFit: 'cover',
                    cursor: 'pointer',
                    borderRadius: 1,
                    opacity: currentIndex === (videos.length + steamScreenshots.length + index) ? 1 : 0.75,
                    border: currentIndex === (videos.length + steamScreenshots.length + index) ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
                    transition: 'opacity 0.2s',
                    '&:hover': {
                      opacity: 1,
                    },
                  }}
                />
              ))}
              {remainingCount > 0 && (
                <Box
                  onClick={handleLoadMore}
                  sx={{
                    width: isMobile ? '90px' : '160px',
                    height: isMobile ? '60px' : '90px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 1,
                    cursor: 'pointer',
                    color: 'white',
                    fontSize: isMobile ? '0.8rem' : '1rem',
                    fontWeight: 'bold',
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  }}
                >
                  {loadingMore ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body1" component="div">
                        +{remainingCount}
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                        全{totalCount}枚
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <IconButton
              onClick={handleNextImage}
              disabled={currentIndex === videos.length + steamScreenshots.length + images.length - 1}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: isMobile ? '32px' : '48px',
                height: '100px',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                cursor: currentIndex === videos.length + steamScreenshots.length + images.length - 1 ? 'default' : 'pointer',
                opacity: currentIndex === videos.length + steamScreenshots.length + images.length - 1 ? 0.5 : 1,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: currentIndex === videos.length + steamScreenshots.length + images.length - 1 ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.7)',
                },
              }}
            >
              <ArrowForwardIosIcon sx={{ color: 'white' }} />
            </IconButton>
          </Box>

          {selectedMedia?.type === 'image' && selectedMedia.original_url && (
            <Button
              onClick={(e) => handleViewOriginal(e, selectedMedia.original_url!)}
              sx={{
                position: 'absolute',
                bottom: 136,
                right: 12,
                color: 'white',
                fontSize: '0.6rem',
                textTransform: 'none',
                backgroundColor: 'rgba(0, 0, 0, 0.25)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
              }}
            >
              View original
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GameGallerySection;